import React, { useState } from "react";
import { Download, CheckCircle, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

import { CONTACT } from "@/lib/company";

// Igual que en el formulario de contacto: usa FormSubmit.co (gratuito, sin
// backend) para reenviar el lead directo al correo del negocio.
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${encodeURIComponent(CONTACT.email)}`;

export default function LeadMagnet() {
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const download = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error("Por favor completa nombre y email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        FORM_ENDPOINT,
        {
          name,
          email,
          recurso: "Checklist impuestos 2026",
          _subject: `Nuevo lead (checklist) desde greatsolutionsus.com - ${name}`,
          _template: "table",
        },
        { headers: { Accept: "application/json" } }
      );
      setSubmitted(true);
      // Trigger download after brief delay
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = "/checklist-impuestos-2026.html";
        link.target = "_blank";
        link.rel = "noopener";
        link.click();
      }, 500);
    } catch (err) {
      toast.error("Ocurrió un problema. Intenta llamar directamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="recursos" className="py-24 lg:py-32 bg-[#f8fafc]" data-testid="lead-magnet-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-5 gap-10 items-center bg-white border border-[#e2e8f0] rounded-sm p-8 lg:p-12 reveal">
          <div className="md:col-span-2 relative">
            <div className="bg-gradient-to-br from-[#0B2D5C] to-[#050F26] rounded-sm p-8 aspect-[3/4] flex flex-col justify-between shadow-2xl relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#C89B2C]/20 rounded-full blur-2xl"></div>
              <div>
                <div className="text-xs text-[#C89B2C] font-semibold tracking-[0.2em] uppercase mb-2">
                  Recurso gratuito
                </div>
                <FileText className="text-white/80 mt-4" size={40} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-display text-white text-xl font-bold leading-tight mb-2">
                  Checklist para preparar tus impuestos 2026
                </h4>
                <p className="text-white/60 text-xs">by Great Solutions US</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-3">
              Descarga gratis
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] leading-[1.1] mb-4">
              Checklist gratis para preparar tus impuestos 2026
            </h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              Documento imprimible con todos los formularios, comprobantes y datos que necesitas tener listos antes de
              tu cita de preparación de impuestos. Aplica para personas y pequeños negocios.
            </p>

            <ul className="space-y-2 mb-8">
              {[
                "Lista completa de documentos",
                "Checklist por tipo de ingreso",
                "Deducciones comunes que puedes aplicar",
                "Datos y plazos importantes 2026",
              ].map((it) => (
                <li key={it} className="flex items-center gap-2 text-sm text-[#334155]">
                  <CheckCircle size={16} className="text-[#C89B2C]" strokeWidth={2} />
                  {it}
                </li>
              ))}
            </ul>

            <Button
              onClick={() => setModalOpen(true)}
              className="bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm h-12 px-7"
              data-testid="lead-magnet-cta"
            >
              <Download size={18} className="mr-2" />
              Descargar checklist gratis
            </Button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] bg-[#0f172a]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !submitted && setModalOpen(false)}
          data-testid="lead-magnet-modal"
        >
          <div
            className="bg-white rounded-sm max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-4 right-4 text-[#475569] hover:text-[#0f172a]"
              data-testid="lead-magnet-close"
            >
              <X size={20} />
            </button>

            {!submitted ? (
              <>
                <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-3">
                  Casi listo
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0f172a] mb-2">
                  Cuéntanos a quién enviar el checklist
                </h3>
                <p className="text-sm text-[#475569] mb-6">
                  Recibe el checklist en tu email. Sin spam, solo información útil.
                </p>

                <form onSubmit={download} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#0f172a] mb-1 block">Tu nombre</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nombre completo"
                      className="h-11 border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                      data-testid="lead-magnet-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0f172a] mb-1 block">Tu email</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="h-11 border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                      data-testid="lead-magnet-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm"
                    data-testid="lead-magnet-submit"
                  >
                    {loading ? "Enviando..." : "Descargar checklist"}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-[#22c55e]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-[#22c55e]" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0f172a] mb-2">
                  ¡Tu descarga comenzó!
                </h3>
                <p className="text-sm text-[#475569] mb-6">
                  El checklist se abrió en una nueva pestaña. Si no comenzó,{" "}
                  <a
                    href="/checklist-impuestos-2026.html"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#0B2D5C] underline"
                  >
                    haz clic aquí
                  </a>
                  .
                </p>
                <Button
                  onClick={() => {
                    setModalOpen(false);
                    setSubmitted(false);
                    setName("");
                    setEmail("");
                  }}
                  className="w-full h-11 bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm"
                  data-testid="lead-magnet-done"
                >
                  Listo
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
